import { useState } from 'react';

import { useAppDispatch, useAppSelector } from 'hooks';

import {
  SimpleGrid,
  Box,
  Text,
  Alert,
  AlertIcon,
  Button,
  Icon,
  Flex,
  Collapse,
  useColorModeValue,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

import FormInput from 'components/fields/auto/FormInput';

import { SECTIONS } from 'views/auto_form/constants';

import { APIPersonalAutoApplicationFormVehicle } from 'types/api.types';

import { changeVehicleLessor } from 'reducers/autoPersonalFormReducer';
import {
  isNonOntarioProvince,
  PROVINCE_NEW_BRUNSWICK,
} from 'variables/constants';
import isSameAsMailingAddress from 'views/auto_form/sections/Vehicles/helpers/isSameAsMailingAddress';

const Lessor = ({
  vehicle,
  index,
}: {
  vehicle: APIPersonalAutoApplicationFormVehicle;
  index: number;
}) => {
  const dispatch = useAppDispatch();
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const province = useAppSelector((state) => state.autoPersonalForm.province);
  const applicant = useAppSelector(
    (state) => state.autoPersonalForm.data.applicant,
  );
  const isNonOntario = isNonOntarioProvince(province);
  const isDisabled =
    province === PROVINCE_NEW_BRUNSWICK && Boolean(vehicle.is_owend);

  const lessor = vehicle.lessor;
  const isMandatory = lessor._info.is_mandatory;
  const hasValues = Boolean(
    lessor.name ||
    lessor.address ||
    lessor.city ||
    lessor.province_code ||
    lessor.postal_code,
  );
  const [isLessorCollapsed, setIsLessorCollapsed] = useState(
    !(isMandatory || hasValues),
  );

  const isSameAsMailing =
    isNonOntario && isSameAsMailingAddress(lessor, applicant);

  const onLessorChange = (event: any) => {
    const { name, value } = event.target;

    dispatch(
      changeVehicleLessor({
        index,
        name,
        value,
      }),
    );
  };

  return (
    <Box p="20px" py="10px" borderTop="1px solid" borderColor={borderColor}>
      <Flex justifyContent="space-between" alignItems="center">
        <Box fontWeight="bold" fontSize="l">
          Lessor Information
        </Box>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsLessorCollapsed(!isLessorCollapsed)}
          rightIcon={
            <Icon as={isLessorCollapsed ? ChevronDownIcon : ChevronUpIcon} />
          }
        >
          {isLessorCollapsed
            ? 'Show Lessor Information'
            : 'Hide Lessor Information'}
        </Button>
      </Flex>
      <Collapse in={!isLessorCollapsed} animateOpacity>
        <Box my="10px">
          <SimpleGrid columns={5} spacing="20px">
            <FormInput
              section={SECTIONS.VEHICLES}
              label="Name"
              subLabel={`Vehicle ${index + 1} Lessor`}
              name="name"
              value={lessor.name}
              onChange={onLessorChange}
              required={isMandatory}
              disabled={isDisabled}
            />
            <FormInput
              section={SECTIONS.VEHICLES}
              label="Address"
              subLabel={`Vehicle ${index + 1} Lessor`}
              name="address"
              value={lessor.address}
              onChange={onLessorChange}
              required={isMandatory}
              disabled={isDisabled}
            />
            <FormInput
              section={SECTIONS.VEHICLES}
              label="City"
              subLabel={`Vehicle ${index + 1} Lessor`}
              name="city"
              value={lessor.city}
              onChange={onLessorChange}
              required={isMandatory}
              disabled={isDisabled}
            />
            <FormInput
              section={SECTIONS.VEHICLES}
              label="Province code"
              subLabel={`Vehicle ${index + 1} Lessor`}
              name="province_code"
              value={lessor.province_code}
              onChange={onLessorChange}
              required={isMandatory}
              disabled={isDisabled}
            />
            <FormInput
              section={SECTIONS.VEHICLES}
              label="Postal code"
              subLabel={`Vehicle ${index + 1} Lessor`}
              name="postal_code"
              value={lessor.postal_code}
              onChange={onLessorChange}
              required={isMandatory}
              disabled={isDisabled}
            />
          </SimpleGrid>

          {isSameAsMailing && (
            <Text color="red.600" fontSize="sm" mt="10px">
              Lessor address must not be the same as the mailing address
            </Text>
          )}

          {isMandatory && (
            <Alert status="warning" fontSize="12px" mt="10px">
              <AlertIcon />
              <Box>
                "#5a Rent or Lease" coverage detected, lessor information is
                mandatory.
              </Box>
            </Alert>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default Lessor;
